import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type { Boat, BookingPolicy, YearMonth } from "../../../../common/src";
import { todayInTimeZone, yearMonthOf } from "../../../../common/src";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { TripParams } from "../../routes";
import { routeToHash } from "../../routes";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import type { DaySelection } from "../../utils/selection";
import { nextSelection } from "../../utils/selection";
import { Card } from "../shared/Card";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { BoatSummary } from "./BoatSummary";
import { CalendarLegend, MonthCalendar, calendarRange } from "./MonthCalendar";
import { ReservationForm } from "./ReservationForm";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  back: {
    display: "inline-block",
    marginBottom: 14,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(300px, 1fr)",
    gap: 20,
    alignItems: "start",
    "@media (max-width: 980px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  side: { display: "grid", gap: 20 },
  refreshing: {
    fontSize: 13,
    color: theme.color.muted,
    minHeight: 18,
    marginTop: 8,
  },
}));

const browserToday = (): string =>
  todayInTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

interface BoatCalendarProps {
  boat: Boat;
  policy: BookingPolicy | undefined;
  trip: TripParams;
}

const BoatCalendar = ({ boat, policy, trip }: BoatCalendarProps) => {
  const classes = useStyles();
  // Open on the searched month, with the searched days pre-selected.
  const [month, setMonth] = useState<YearMonth>(() =>
    yearMonthOf(trip.startDate ?? browserToday())
  );
  const [selection, setSelection] = useState<DaySelection>({
    startDate: trip.startDate,
    endDate: trip.endDate,
  });

  const loadCalendar = useCallback(
    (signal: AbortSignal) =>
      trpc.reservation.calendar.query(
        { boatId: boat.id, ...calendarRange(month) },
        { signal }
      ),
    [boat.id, month]
  );
  const calendar = useAsyncData(loadCalendar);

  return (
    <div className={classes.layout}>
      <Card title={`${boat.name} availability`} actions={<CalendarLegend />}>
        {calendar.error && (
          <LoadError message={calendar.error} onRetry={calendar.reload} />
        )}
        {!boat.isActive && (
          <Notice kind="info">
            This boat is not accepting reservations right now.
          </Notice>
        )}
        <MonthCalendar
          month={month}
          onMonthChange={setMonth}
          calendar={calendar.data}
          selection={selection}
          onSelectDay={(date) =>
            setSelection((prev) => nextSelection(prev, date))
          }
        />
        <div className={classes.refreshing} aria-live="polite">
          {calendar.loading ? "Loading availability…" : ""}
        </div>
      </Card>
      <div className={classes.side}>
        {boat.isActive && (
          <ReservationForm
            boat={boat}
            selection={selection}
            onSelectionChange={setSelection}
            initialPartySize={trip.partySize}
          />
        )}
        <BoatSummary boat={boat} policy={policy} />
      </div>
    </div>
  );
};

/** One boat's calendar, reached from the search page (workflow: boat first). */
export const BoatCalendarPage = ({
  boatId,
  trip,
}: {
  boatId: number;
  trip: TripParams;
}) => {
  const classes = useStyles();
  const load = useCallback(
    async (signal: AbortSignal) => {
      const [boat, policy] = await Promise.all([
        trpc.boat.get.query({ id: boatId }, { signal }),
        trpc.bookingPolicy.get.query(undefined, { signal }),
      ]);
      return { boat, policy };
    },
    [boatId]
  );
  const data = useAsyncData(load);

  return (
    <div>
      <a className={classes.back} href={routeToHash({ page: "find", trip })}>
        ← All boats
      </a>
      {data.error && <LoadError message={data.error} onRetry={data.reload} />}
      {data.loading && !data.data && <Loading label="Loading boat…" />}
      {data.data && (
        // Remount when switching boats so month and selection reset.
        <BoatCalendar
          key={data.data.boat.id}
          boat={data.data.boat}
          policy={data.data.policy}
          trip={trip}
        />
      )}
    </div>
  );
};
